import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '@/lib/api/client'
import type { FacilitySummary } from '@/types/facility'
import type { FacilitySearchFilter } from '@/types/facility'
import FacilityCard from './FacilityCard'

interface Props {
  filter: FacilitySearchFilter
  centerLat: number
  centerLng: number
  selectedFacility?: (FacilitySummary & { ts: number }) | null
  onFacilityClick?: (facility: FacilitySummary) => void
}

declare global {
  interface Window {
    naver: any
    MarkerClustering: any
  }
}

/** 뷰포트 API 한 번에 가져올 최대 건수 */
const MAP_PAGE_SIZE = 500

/** 등급별 마커 색상 */
function gradeColor(grade?: string | null) {
  if (grade === 'A') return '#087F5B'
  if (grade === 'B') return '#1864AB'
  if (grade === 'C') return '#E67700'
  if (grade === 'D') return '#D9480F'
  if (grade === 'E') return '#C92A2A'
  return '#78716C'
}

/** 클러스터 아이콘 생성 */
function clusterIcon(size: number, bg: string) {
  return {
    content: `<div style="
      cursor:pointer;width:${size}px;height:${size}px;line-height:${size}px;
      font-size:${size > 44 ? 13 : 11}px;color:white;text-align:center;font-weight:700;
      background:${bg};border-radius:50%;border:2.5px solid white;
      box-shadow:0 2px 8px rgba(0,0,0,0.25);
    "></div>`,
    size: new window.naver.maps.Size(size, size),
    anchor: new window.naver.maps.Point(size / 2, size / 2),
  }
}

/** 두 좌표 간 거리 (km) — Haversine 간이 계산 */
function distanceBetween(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export default function FacilityMapView({ filter, centerLat, centerLng, selectedFacility, onFacilityClick }: Props) {
  const navigate = useNavigate()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<any>(null)
  const clusterRef = useRef<any>(null)
  const markersRef = useRef<any[]>([])
  const fetchController = useRef<AbortController | null>(null)
  const isPanningRef = useRef(false)
  const highlightMarkerRef = useRef<any>(null)
  const [selected, setSelected] = useState<FacilitySummary | null>(null)
  const [mapFacilities, setMapFacilities] = useState<FacilitySummary[]>([])
  const [loading, setLoading] = useState(false)

  /** 지도 뷰포트 기준으로 시설 API 호출 */
  const fetchForViewport = useCallback(async () => {
    // panTo 애니메이션 중에는 재검색 하지 않음
    if (isPanningRef.current) return

    const map = mapInstance.current
    if (!map || !window.naver?.maps) return

    const bounds = map.getBounds()
    if (!bounds) return

    // 뷰포트 중심점과 반경 계산
    const ne = bounds.getNE()
    const sw = bounds.getSW()
    const vpCenterLat = (ne.lat() + sw.lat()) / 2
    const vpCenterLng = (ne.lng() + sw.lng()) / 2
    const vpRadiusKm = Math.ceil(distanceBetween(vpCenterLat, vpCenterLng, ne.lat(), ne.lng()) * 1.1)

    // 이전 요청 취소
    if (fetchController.current) fetchController.current.abort()
    const controller = new AbortController()
    fetchController.current = controller

    // 검색 파라미터 구성 (필터 유지)
    const params = new URLSearchParams()
    params.append('latitude', String(vpCenterLat))
    params.append('longitude', String(vpCenterLng))
    params.append('radiusKm', String(vpRadiusKm))
    params.append('sortBy', 'DISTANCE')
    params.append('page', '0')
    params.append('size', String(MAP_PAGE_SIZE))
    if (filter.keyword) params.append('name', filter.keyword)
    if (filter.type && filter.type.length > 0 && filter.type[0]) params.append('facilityType', filter.type[0])
    if (filter.grade && filter.grade.length > 0) {
      filter.grade.forEach(g => params.append('evaluationGrade', g))
    }
    if (filter.hasDementiaCare) params.append('hasDementiaCare', 'true')
    if (filter.hasVacancy) params.append('hasVacancy', 'true')
    if (filter.hasPrograms) params.append('hasPrograms', 'true')

    try {
      setLoading(true)
      const res = await apiClient.get(`/facilities?${params.toString()}`, {
        signal: controller.signal,
      })
      if (!controller.signal.aborted) {
        setMapFacilities(res.data.data.content)
      }
    } catch {
      // 취소 또는 에러 무시
    } finally {
      if (!controller.signal.aborted) setLoading(false)
    }
  }, [filter])

  /** 마커 렌더링 */
  const renderMarkers = useCallback((facilities: FacilitySummary[]) => {
    const map = mapInstance.current
    if (!map || !window.naver?.maps) return

    // 기존 클러스터/마커 제거
    // 네이버 MarkerClustering.setMap(null) 은 내부 상태가 정리된 시점에
    // get('capitalize') null 참조로 throw 할 수 있다(라이브 관측 2026-08-05).
    // try 밖에서 터지면 React 트리가 통째로 크래시되므로 방어한다.
    if (clusterRef.current) {
      try { clusterRef.current.setMap(null) } catch { /* 네이버 라이브러리 내부 null 참조 무시 */ }
      clusterRef.current = null
    }
    markersRef.current.forEach(m => m.setMap(null))
    markersRef.current = []

    if (facilities.length === 0) return

    const markers: any[] = []
    facilities.forEach((facility) => {
      if (!facility.latitude || !facility.longitude) return
      const grade = facility.evaluationGrade
      const color = gradeColor(grade)

      const marker = new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(facility.latitude, facility.longitude),
        icon: {
          content: `<div style="position:relative;cursor:pointer;">
            <div style="
              background:${color};color:white;padding:2px 6px;border-radius:12px;
              font-size:11px;font-weight:700;white-space:nowrap;
              box-shadow:0 2px 4px rgba(0,0,0,0.2);border:2px solid white;
              width:fit-content;
            ">${grade || '●'}</div>
            <div style="
              position:absolute;left:50%;top:100%;transform:translateX(-50%);
              margin-top:2px;max-width:88px;
              padding:1px 5px;border-radius:3px;
              background:rgba(255,255,255,0.92);color:#1f2937;
              font-size:10px;font-weight:600;white-space:nowrap;
              overflow:hidden;text-overflow:ellipsis;
              box-shadow:0 1px 2px rgba(0,0,0,0.15);
              pointer-events:none;
            ">${facility.name}</div>
          </div>`,
          anchor: new window.naver.maps.Point(15, 15),
        },
        zIndex: 100,
      })

      window.naver.maps.Event.addListener(marker, 'click', () => {
        setSelected(facility)
        onFacilityClick?.(facility)
      })

      markers.push(marker)
    })

    markersRef.current = markers

    // MarkerClustering 은 외부 라이브러리(네이버) — 봇/headless 환경이나 지도 미준비
    // 시점에 내부에서 throw 할 수 있다(예: MarkerClustering.get 의 null 참조).
    // 그 예외가 렌더 이펙트로 전파되면 React 트리가 통째로 깨져 빈 화면이 되므로
    // (bingbot 의 /facilities 크롤 시 발생), 실패하면 일반 마커로 폴백한다.
    let clustered = false
    if (window.MarkerClustering && markers.length > 0) {
      try {
        clusterRef.current = new window.MarkerClustering({
          map,
          markers,
          minClusterSize: 2,
          maxZoom: 15,
          gridSize: 120,
          disableClickZoom: false,
          averageCenter: true,
          icons: [
            clusterIcon(36, '#3B82F6'),
            clusterIcon(44, '#2563EB'),
            clusterIcon(52, '#1D4ED8'),
          ],
          indexGenerator: [10, 50, 200],
          stylingFunction: (clusterMarker: any, count: number) => {
            const el = clusterMarker.getElement()
            if (el) {
              const div = el.querySelector('div:first-child')
              if (div) div.innerText = count
            }
          },
        })
        clustered = true
      } catch {
        // 클러스터링 실패 — 일반 마커로 폴백 (페이지 크래시 방지)
        clusterRef.current = null
      }
    }
    if (!clustered) {
      markers.forEach(m => m.setMap(map))
    }
  }, [onFacilityClick])

  // mapFacilities 변경 시 마커 갱신
  useEffect(() => {
    renderMarkers(mapFacilities)
  }, [mapFacilities, renderMarkers])

  // 외부에서 선택된 시설로 지도 이동 + 강조 마커 (좌표 직접 전달)
  useEffect(() => {
    if (!selectedFacility) return

    const tryHighlight = () => {
      if (!mapInstance.current || !window.naver?.maps) {
        setTimeout(tryHighlight, 200)
        return
      }

      // 기존 강조 마커 제거
      if (highlightMarkerRef.current) {
        highlightMarkerRef.current.setMap(null)
        highlightMarkerRef.current = null
      }

      const { name, latitude: lat, longitude: lng } = selectedFacility
      if (lat == null || lng == null) return

      // panTo 시 idle 이벤트에서 재검색 방지
      isPanningRef.current = true
      mapInstance.current.panTo(new window.naver.maps.LatLng(lat, lng))
      setTimeout(() => { isPanningRef.current = false }, 1000)

      // 리스트 카드 클릭 시에도 하단 시설정보 카드를 표시(마커 클릭과 동일 동작)
      setSelected(selectedFacility)

      // 강조 마커 (큰 빨간 핀)
      highlightMarkerRef.current = new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(lat, lng),
        map: mapInstance.current,
        icon: {
          content: `<div style="
            background:#EF4444;color:white;padding:4px 10px;border-radius:16px;
            font-size:13px;font-weight:700;white-space:nowrap;
            box-shadow:0 3px 8px rgba(239,68,68,0.4);border:3px solid white;
            cursor:pointer;
          ">${name}</div>`,
          anchor: new window.naver.maps.Point(50, 20),
        },
        zIndex: 300,
      })
    }

    tryHighlight()
  }, [selectedFacility])

  // 지도 초기화
  // 네이버 지도 SDK(`window.naver.maps`)는 비동기 로드되므로 SDK 준비를 폴링으로 기다린다.
  // 위치 권한 거부 후 fallback 좌표가 빠르게 세팅되는 경로에서 SDK 미로드로 지도가 안 뜨는 버그 대응.
  useEffect(() => {
    let disposed = false
    let pollTimer: ReturnType<typeof setInterval> | null = null

    const initMap = () => {
      if (disposed || !mapRef.current || !window.naver?.maps) return

      const map = new window.naver.maps.Map(mapRef.current, {
        center: new window.naver.maps.LatLng(centerLat, centerLng),
        zoom: 14,
        zoomControl: true,
        zoomControlOptions: {
          position: window.naver.maps.Position.TOP_RIGHT,
          style: window.naver.maps.ZoomControlStyle.SMALL,
        },
      })
      mapInstance.current = map

      // 현재 위치 마커 (파란 점)
      new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(centerLat, centerLng),
        map,
        icon: {
          content: '<div style="width:14px;height:14px;background:#4C6EF5;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>',
          anchor: new window.naver.maps.Point(7, 7),
        },
        zIndex: 200,
      })

      // 지도 클릭 시 선택 해제
      window.naver.maps.Event.addListener(map, 'click', () => setSelected(null))

      // 지도 이동/줌 완료 시 뷰포트 기준 API 재호출
      window.naver.maps.Event.addListener(map, 'idle', fetchForViewport)

      // 초기 로드
      fetchForViewport()
    }

    // SDK 가 이미 로드되어 있으면 즉시 초기화, 아니면 100ms 간격 폴링
    if (window.naver?.maps) {
      initMap()
    } else {
      pollTimer = setInterval(() => {
        if (window.naver?.maps) {
          if (pollTimer) clearInterval(pollTimer)
          pollTimer = null
          initMap()
        }
      }, 100)
    }

    return () => {
      disposed = true
      if (pollTimer) clearInterval(pollTimer)
      if (fetchController.current) fetchController.current.abort()
      if (clusterRef.current) {
        try { clusterRef.current.setMap(null) } catch { /* 네이버 라이브러리 내부 null 참조 무시 */ }
        clusterRef.current = null
      }
      markersRef.current.forEach(m => m.setMap(null))
      markersRef.current = []
      mapInstance.current = null
    }
  }, [centerLat, centerLng, fetchForViewport])

  // isolate: 네이버 지도 SDK가 주입하는 컨트롤(줌·축척·로고·저작권)은 높은 inline
  // z-index를 갖는데, 이 래퍼가 독립 stacking context를 만들어야 그 컨트롤들이
  // 밖으로 튀어나와 주소검색 시트(z-50) 위에 겹쳐 그려지는 문제를 막는다.
  return (
    <div className="relative h-full isolate">
      {/* 로딩 표시 */}
      {loading && (
        <div className="absolute top-3 right-3 z-10">
          <div className="rounded-full bg-white/90 backdrop-blur-sm px-3 py-1.5 shadow-md text-caption text-cb-gray-500">
            불러오는 중...
          </div>
        </div>
      )}

      <div ref={mapRef} className="w-full h-full rounded-2xl overflow-hidden shadow-sm" />

      {/* 선택된 시설 카드 */}
      {selected && (
        <div className="absolute bottom-4 left-3 right-3 z-10 animate-fade-slide-up shadow-lg rounded-2xl">
          <FacilityCard
            facility={selected}
            onCardClick={(id) => navigate(`/facilities/${id}`)}
          />
        </div>
      )}
    </div>
  )
}
